/**
 * Cloudinary Service
 * Handles PDF upload and deletion for salary slips
 */

// Cloudinary configuration from environment variables
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET

/**
 * Validate Cloudinary configuration
 */
const validateConfig = () => {
  if (!CLOUDINARY_CLOUD_NAME) {
    throw new Error('Cloudinary cloud name not configured. Please set VITE_CLOUDINARY_CLOUD_NAME in .env')
  }
  if (!CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary upload preset not configured. Please set VITE_CLOUDINARY_UPLOAD_PRESET in .env')
  }
}

/**
 * Validate PDF file before upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export const validatePDF = (file) => {
  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file selected' }
  }

  // Check file type
  const validTypes = ['application/pdf']
  if (!validTypes.includes(file.type)) {
    return { valid: false, error: 'Only PDF files are allowed' }
  }

  // Check file size (10MB = 10,485,760 bytes)
  const maxSize = 10 * 1024 * 1024 // 10MB
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' }
  }

  return { valid: true }
}

/**
 * Upload salary slip PDF to Cloudinary
 * @param {File} file - PDF file to upload
 * @param {string} employeeId - Employee ID for folder structure
 * @param {string} month - Month name
 * @param {number} year - Year
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const uploadSalarySlip = async (file, employeeId, month, year) => {
  try {
    // Validate configuration
    validateConfig()

    // Validate file
    const validation = validatePDF(file)
    if (!validation.valid) {
      return { success: false, error: validation.error }
    }

    // Create form data
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET)

    // Create folder structure: salary_slips/{employeeId}/{year}
    const folder = `salary_slips/${employeeId}/${year}`
    formData.append('folder', folder)

    // Create public ID: {month}_salary_slip
    const publicId = `${month.toLowerCase()}_salary_slip`
    formData.append('public_id', publicId)

    // Set resource type to raw for PDF
    formData.append('resource_type', 'raw')

    // Upload to Cloudinary
    const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/raw/upload`

    const response = await fetch(cloudinaryUrl, {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('Cloudinary upload error:', errorData)
      return {
        success: false,
        error: errorData.error?.message || 'Failed to upload file to Cloudinary'
      }
    }

    const data = await response.json()

    return {
      success: true,
      data: {
        cloudinaryUrl: data.secure_url,
        publicId: data.public_id,
        fileSize: data.bytes,
        format: data.format,
        resourceType: data.resource_type
      }
    }
  } catch (error) {
    console.error('Cloudinary service error:', error)
    return {
      success: false,
      error: error.message || 'An error occurred while uploading the file'
    }
  }
}

/**
 * Delete salary slip from Cloudinary
 * @param {string} publicId - Cloudinary public ID
 * @returns {Promise<{ success: boolean, warning?: string, deletedFromFirestore?: boolean, deletedFromCloudinary?: boolean, error?: string }>}
 */
export const deleteSalarySlip = async (publicId) => {
  try {
    // Validate configuration
    validateConfig()

    // Note: Deletion requires signed requests in production
    // For now, we'll rely on Firestore deletion and Cloudinary auto-cleanup
    // In production, implement server-side deletion with API secret


    return {
      success: true,
      warning: 'Salary slip removed from portal. PDF remains in storage. Contact admin to permanently delete from Cloudinary if needed.',
      deletedFromFirestore: true,
      deletedFromCloudinary: false
    }
  } catch (error) {
    console.error('Cloudinary deletion error:', error)
    return {
      success: false,
      error: error.message || 'An error occurred while deleting the file'
    }
  }
}

/**
 * Format file size from bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B'

  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = (bytes / Math.pow(1024, i)).toFixed(2)

  return `${size} ${sizes[i]}`
}

/**
 * Get download filename from month and year
 * @param {string} month - Month name
 * @param {number} year - Year
 * @param {string} employeeName - Employee name (optional)
 * @returns {string} - Formatted filename
 */
export const getDownloadFilename = (month, year, employeeName = null) => {
  const base = employeeName
    ? `${employeeName}_Salary_Slip_${month}_${year}`
    : `Salary_Slip_${month}_${year}`

  return `${base}.pdf`
}
